// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { LRUCache, LRUCacheOptions } from 'typescript-lru-cache';
import { ACSCache } from './ACSCache';
import { ACSKey, SerializedACSContainer } from './ACSTypes';

const DEFAULT_CACHE_OPTIONS: LRUCacheOptions<ACSKey, SerializedACSContainer> = {
  maxSize: 50,
  entryExpirationTimeInMS: 60 * 60 * 1000, // 60 minutes
};

export class ACSCacheLRU extends ACSCache {
  private readonly cache = new LRUCache<ACSKey, SerializedACSContainer>(DEFAULT_CACHE_OPTIONS);

  public clear(): Promise<void> {
    return new Promise((resolve) => {
      this.cache.clear();
      resolve();
    });
  }

  public get(key: ACSKey): Promise<SerializedACSContainer | null> {
    return new Promise((resolve) => {
      const cachedACSContainer = this.cache.get(key);
      resolve(cachedACSContainer);
    });
  }

  public getAllKeys(): Promise<ACSKey[]> {
    return new Promise((resolve) => {
      const keys: ACSKey[] = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const key of this.cache.keys()) {
        keys.push(key);
      }

      resolve(keys);
    });
  }

  public has(key: ACSKey): Promise<boolean> {
    return new Promise((resolve) => {
      resolve(this.cache.has(key));
    });
  }

  public remove(key: ACSKey): Promise<void> {
    return new Promise((resolve) => {
      this.cache.delete(key);
      resolve();
    });
  }

  public set(serializedContainer: SerializedACSContainer): Promise<void> {
    return new Promise((resolve) => {
      this.cache.set(serializedContainer.key, serializedContainer);
      resolve();
    });
  }
}
